// The tv's "this bout" card (#557): the live bout's numbers, drawn from the turns as they land on the screen — the
// tokens per turn against the limit, each side's drift and the swing between them from the embeddings, in one chart
// (boutSvg), and beside it each side's shape from the embeddings over the bout so far (shapeSvg: a pentagon), the turn
// and the shot clock, and the two sides measured in a table. The card is rendered empty by the server
// (pages/ring.rs bout_card); the history comes off the transcript's rows (a mid-bout load), and every turn after that
// arrives as `basho:turn` from the player (live.ts). A replay plays its turns through the same events.
import { $, esc } from "./dom.ts";
import { ring } from "./tv.ts";
import { limits, other, sealed } from "./wire.ts";

/** one turn as the card keeps it */
export type Row = {
  turn: number;
  side: Side;
  tokens: number;
  content: string;
  drift?: number;
  swing?: number;
  agenda?: number;
  pull?: number;
  fresh?: number;
};
/** the measures off the embeddings a row may carry (cosine distances, 0..2), as the server names them */
const MEASURES = ["drift", "swing", "agenda", "pull", "fresh"] as const;

/** how often the shot clock is redrawn, ms */
const TICK = 250;
/** the clock reads late from this share of the cap */
const LATE = 0.8;

const num = (s: string | undefined): number | undefined => {
  if (s === undefined || s === "") return undefined;
  const v = +s;
  return Number.isFinite(v) ? v : undefined;
};

/** the transcript's rows as the server (or the player) put them on the page: the thinking dots are not a turn */
export function readRows(list: ParentNode): Row[] {
  const out: Row[] = [];
  for (const li of list.querySelectorAll("li.msg:not(.thinking)")) {
    const d = (li as HTMLElement).dataset;
    if (d.side !== "east" && d.side !== "west") continue;
    const r: Row = {
      turn: +(d.turn || 0),
      side: d.side,
      tokens: +(d.tokens || 0),
      content: li.querySelector(".body")?.textContent || "",
    };
    for (const k of MEASURES) {
      const v = num(d[k]);
      if (v !== undefined) r[k] = v;
    }
    out.push(r);
  }
  return out;
}

/** a turn event as a row */
const rowOf = (m: TurnEvent): Row => {
  const r: Row = { turn: m.turn, side: m.side, tokens: m.tokens, content: m.content };
  for (const k of MEASURES) if (m[k] !== undefined) r[k] = m[k];
  return r;
};

const f3 = (n: number) => (Math.round(n * 1000) / 1000).toString();

/** the fewest turns the width is drawn for: a bout fills its chart from the first turns and compresses as it goes on, never past the edge */
const REVEAL = 12;
/** the turns the width stands for: the bout so far, and at least REVEAL */
const spanOf = (rows: Row[]) =>
  Math.max(
    REVEAL,
    rows.reduce((m, r) => Math.max(m, r.turn), 0),
  );
/** the chart's box: viewBox 0 0 100 CHART_H, the midline between the sides, a margin above and below */
const CHART_H = 64,
  CHART_PAD = 3;
/** the swing band's widest, as a share of a side's half: it runs between the sides and never swamps them */
const SWING_SHARE = 0.42;

/** where a turn sits across the width (the bout so far) */
const px = (turn: number, span: number) => ((turn - 0.5) / span) * 100;
const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
/** the scale of a distance: the bout's own largest so far, never under SPARK_FLOOR, so a quiet bout still shows its shape */
const SPARK_FLOOR = 0.4;
const topOf = (rows: Row[], key: "drift" | "swing") => Math.max(SPARK_FLOOR, ...rows.map((r) => r[key] ?? 0));

/**
 * The bout in one chart (viewBox 0 0 100 CHART_H), a column a turn across the bout so far (spanOf):
 * - tokens: each turn's bar from the midline, east up and west down, its length against the limit; over the limit, gold;
 * - drift: a line a side through its own half, how far that side moved from its own last message, a dot a turn;
 * - swing: a gold band along the midline, as thick as each message moved from the other side's last.
 * `hot` is the turn under the pointer: its column lit. The limit is a dashed line at each edge.
 */
export function boutSvg(rows: Row[], maxTokens: number, hot = 0): string {
  const span = spanOf(rows),
    slot = 100 / span,
    w = slot * 0.62,
    mid = CHART_H / 2,
    half = mid - CHART_PAD;
  const sorted = [...rows].sort((a, b) => a.turn - b.turn);
  let s = `<line class="limit" x1="0" y1="${CHART_PAD}" x2="100" y2="${CHART_PAD}"/><line class="limit" x1="0" y1="${CHART_H - CHART_PAD}" x2="100" y2="${CHART_H - CHART_PAD}"/>`;
  // the swing band, under everything: the exchange between the sides
  const sw = sorted.filter((r) => r.swing !== undefined),
    swTop = topOf(sw, "swing"),
    R = half * SWING_SHARE;
  const thick = (r: Row) => Math.max(0.35, clamp01((r.swing as number) / swTop) * R);
  if (sw.length > 1) {
    const upper = sw.map((r) => `${f3(px(r.turn, span))},${f3(mid - thick(r))}`),
      lower = sw.map((r) => `${f3(px(r.turn, span))},${f3(mid + thick(r))}`).reverse();
    s += `<polygon class="swing" points="${upper.join(" ")} ${lower.join(" ")}"/>`;
    s += `<polyline class="swing-edge" points="${upper.join(" ")}"/><polyline class="swing-edge" points="${lower.reverse().join(" ")}"/>`;
  } else if (sw.length === 1) {
    const r = sw[0],
      x = f3(px(r.turn, span));
    s += `<line class="swing-edge solo" x1="${x}" y1="${f3(mid - thick(r))}" x2="${x}" y2="${f3(mid + thick(r))}"/>`;
  }
  s += `<line class="mid" x1="0" y1="${mid}" x2="100" y2="${mid}"/>`;
  // the tokens: a bar a turn, its cap the side's colour at full strength
  for (const r of sorted) {
    const share = Math.min(1, r.tokens / Math.max(1, maxTokens)),
      h = Math.max(0.6, share * half),
      x = (r.turn - 1) * slot + (slot - w) / 2;
    const y = r.side === "east" ? mid - h : mid,
      cap = r.side === "east" ? mid - h : mid + h;
    const cls = `${r.side}${r.tokens > maxTokens ? " over" : ""}${r.turn === hot ? " hot" : ""}`;
    s += `<rect class="bar ${cls}" x="${f3(x)}" y="${f3(y)}" width="${f3(w)}" height="${f3(h)}"/><line class="cap ${cls}" x1="${f3(x)}" y1="${f3(cap)}" x2="${f3(x + w)}" y2="${f3(cap)}"/>`;
  }
  // the drift: each side's own line in its own half, a dot a turn, the latest the largest
  const dTop = topOf(sorted, "drift");
  for (const side of ["east", "west"] as Side[]) {
    const own = sorted.filter((r) => r.side === side && r.drift !== undefined);
    const at = (r: Row) => {
      const d = clamp01((r.drift as number) / dTop) * half;
      return [px(r.turn, span), side === "east" ? mid - d : mid + d];
    };
    if (own.length > 1)
      s += `<polyline class="drift ${side}" points="${own.map((r) => at(r).map(f3).join(",")).join(" ")}"/>`;
    own.forEach((r, i) => {
      const [x, y] = at(r).map(f3);
      s += `<line class="dot ${side}${i === own.length - 1 ? " now" : ""}${r.turn === hot ? " hot" : ""}" x1="${x}" y1="${y}" x2="${x}" y2="${y}"/>`;
    });
  }
  if (hot) s += `<line class="cursor" x1="${f3(px(hot, span))}" y1="0" x2="${f3(px(hot, span))}" y2="${CHART_H}"/>`;
  return s;
}

/** the shape's five axes, clockwise from the top: the word on the chart, and how each side scores on it (0..1 before scaling) */
const AXES = ["script", "steer", "fresh", "drift", "swing"] as const;
type Shape = Record<(typeof AXES)[number], number>;
const mean = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);
const vals = (rows: Row[], k: (typeof MEASURES)[number]) =>
  rows.map((r) => r[k]).filter((v): v is number => v !== undefined);

/**
 * Each axis's range in real bouts: a side's average over 40 live bouts (2026-10-16), from the 5th percentile to the
 * 95th — script a distance to its own preprompt (nearer is more script), steer the other side's lean toward this side's
 * preprompt (its distance to its own less its distance to this side's), the rest the chart's distances. A shape is where
 * a side stands among all bouts, not against the other side: the two differ when they play differently.
 */
const RANGE: Record<(typeof AXES)[number], [number, number]> = {
  script: [0.44, 0.26],
  steer: [-0.13, 0.07],
  fresh: [0.12, 0.28],
  drift: [0.13, 0.33],
  swing: [0.16, 0.36],
};
/** where on its axis a side's lowest shows (so a quiet side is still a shape), and where a measure it has none of sits */
export const SHAPE_FLOOR = 0.08,
  SHAPE_NONE = 0.03;
const placed = (a: (typeof AXES)[number], v: number | undefined) => {
  if (v === undefined) return SHAPE_NONE;
  const [lo, hi] = RANGE[a];
  return SHAPE_FLOOR + (1 - SHAPE_FLOOR) * clamp01((v - lo) / (hi - lo));
};
const meanOf = (xs: number[]) => (xs.length ? mean(xs) : undefined);

/**
 * A side's shape over the bout so far, from the embeddings, each axis 0..1 where it stands in its RANGE:
 * - script: how near its messages stay to its own preprompt;
 * - steer: how far the other side's messages lean toward this side's preprompt rather than their own;
 * - fresh: how far each message stands from the nearest one said before it in the bout, either side's;
 * - drift, swing: the averages of the chart's two.
 */
export function shapeOf(rows: Row[], side: Side): Shape {
  const own = rows.filter((r) => r.side === side),
    theirs = rows.filter((r) => r.side !== side && r.agenda !== undefined && r.pull !== undefined);
  return {
    script: placed("script", meanOf(vals(own, "agenda"))),
    steer: placed("steer", meanOf(theirs.map((r) => (r.agenda as number) - (r.pull as number)))),
    fresh: placed("fresh", meanOf(vals(own, "fresh"))),
    drift: placed("drift", meanOf(vals(own, "drift"))),
    swing: placed("swing", meanOf(vals(own, "swing"))),
  };
}

/** the pentagon's box: viewBox 0 0 100 100, its centre and its reach */
const SHAPE = { cx: 50, cy: 53, r: 34, rings: 3, label: 9 };

/**
 * Each side's shape (shapeOf) as a pentagon on five axes, the two drawn over each other in their colours, scaled together
 * so the largest value either side has on any axis sits on the rim (100%): who covers whom, and where, at a glance.
 */
export function shapeSvg(rows: Row[]): string {
  const sh = { east: shapeOf(rows, "east"), west: shapeOf(rows, "west") };
  const drawn = (["west", "east"] as Side[]).filter((side) => rows.some((r) => r.side === side));
  const top = Math.max(0, ...drawn.flatMap((side) => AXES.map((a) => sh[side][a])));
  const scaled = (v: number) => (top > 0 ? v / top : v);
  const point = (k: number, v: number) => {
    const a = -Math.PI / 2 + (k * 2 * Math.PI) / AXES.length;
    return [SHAPE.cx + Math.cos(a) * SHAPE.r * v, SHAPE.cy + Math.sin(a) * SHAPE.r * v];
  };
  const ring = (v: number) => AXES.map((_, k) => point(k, v).map(f3).join(",")).join(" ");
  let s = "";
  for (let i = 1; i <= SHAPE.rings; i++) s += `<polygon class="grid" points="${ring(i / SHAPE.rings)}"/>`;
  AXES.forEach((name, k) => {
    const [x, y] = point(k, 1),
      [lx, ly] = point(k, 1 + SHAPE.label / SHAPE.r);
    s += `<line class="axis" x1="${SHAPE.cx}" y1="${SHAPE.cy}" x2="${f3(x)}" y2="${f3(y)}"/>`;
    s += `<text class="axis-label" x="${f3(lx)}" y="${f3(ly)}">${name}</text>`;
  });
  for (const side of drawn) {
    const pts = AXES.map((a, k) => point(k, scaled(sh[side][a])).map(f3).join(","));
    s += `<polygon class="shape ${side}" points="${pts.join(" ")}"/>`;
  }
  return s;
}

/** the turn under a point across the chart (0..1 of its width), when there is one */
export function turnAt(rows: Row[], frac: number): Row | undefined {
  const span = spanOf(rows),
    turn = Math.min(span, Math.max(1, Math.floor(clamp01(frac) * span) + 1));
  return rows.find((r) => r.turn === turn);
}

/** a word in the readout with its explanation on hover or focus (home.css .tipped): the card's own words (ring.rs TIP_*) */
const tipped = (word: string, tip: string | undefined) =>
  tip ? `<span class="tipped" tabindex="0" data-tip="${esc(tip)}">${word}</span>` : word;

/** a turn in words, for the line under the chart, each measure behind a swatch of its colour on the chart: the side's for
 * the tokens and the drift (a line), gold for the swing; then the shape beside it; `tips` explain them where they are named */
export function readout(
  r: Row | undefined,
  maxTokens: number,
  tips: { drift?: string; swing?: string; tokens?: string; shape?: string } = {},
): string {
  if (!r) return `<span class="dim">no turn yet</span>`;
  const d = (v: number | undefined) => (v === undefined ? "—" : v.toFixed(2));
  return `<span class="${r.side}">${r.side}</span> <span class="dim">turn</span> <b>${r.turn}</b> <span class="key"><i class="sw ${r.side}${r.tokens > maxTokens ? " over" : ""}"></i><b${r.tokens > maxTokens ? ' class="over"' : ""}>${r.tokens}</b> <span class="dim">${tipped("tokens", tips.tokens)}</span></span> <span class="key"><i class="sw line ${r.side}"></i><span class="dim">${tipped("drift", tips.drift)}</span> <b>${d(r.drift)}</b></span> <span class="key"><i class="sw swing"></i><span class="dim">${tipped("swing", tips.swing)}</span> <b>${d(r.swing)}</b></span> <span class="key"><i class="sw shape"></i><span class="dim">${tipped("shape", tips.shape)}</span></span>`;
}

/** a side's numbers for the card's table */
type SideStats = { turns: number; tokens: number; avg: number; longest: number };

/** a side's numbers from the turns so far */
export function sideStats(rows: Row[], side: Side): SideStats {
  const own = rows.filter((r) => r.side === side),
    tokens = own.reduce((n, r) => n + r.tokens, 0);
  return {
    turns: own.length,
    tokens,
    avg: own.length ? Math.round(tokens / own.length) : 0,
    longest: own.reduce((m, r) => Math.max(m, r.tokens), 0),
  };
}

/**
 * The table: west, the measure, east; the side ahead on a measure marked `lead`. The losing conditions (over the
 * limit, the repeats) are not here: the bout prints them on the turn that loses.
 */
export function fillStats(table: ParentNode, rows: Row[]) {
  const any = { east: rows.some((r) => r.side === "east"), west: rows.some((r) => r.side === "west") };
  const st = { east: sideStats(rows, "east"), west: sideStats(rows, "west") };
  for (const tr of table.querySelectorAll<HTMLElement>("tr[data-stat]")) {
    const key = tr.dataset.stat as keyof SideStats;
    if (!(key in st.east)) continue;
    for (const side of ["east", "west"] as Side[]) {
      const td = tr.querySelector<HTMLElement>(`td.${side}`);
      if (!td) continue;
      const v = st[side][key];
      td.classList.toggle("lead", any[side] && v > st[other(side)][key]);
      td.textContent = any[side] ? String(v) : "—";
    }
  }
}

/**
 * Wire the card under the bout on the page. Reads the rows already on the transcript, then follows the player's turn
 * events; the shot clock ticks from the last turn to arrive while the bout is live. Torn down with the ring.
 */
export function initPanel() {
  const card = $("[data-bout-card]"),
    bout = $("article.bout");
  if (!card || !bout) return;
  const ctrl = new window.AbortController(); // the window's own: under jsdom node's signal is a stranger
  const { signal } = ctrl;
  const { maxTokens } = limits(bout);
  const live = !!bout.dataset.live && !bout.dataset.replay;
  const cap = +(bout.dataset.shotClock || 0) || 0;
  const tips = {
    drift: card.dataset.tipDrift,
    swing: card.dataset.tipSwing,
    tokens: card.dataset.tipTokens,
    shape: card.dataset.tipShape,
  };
  const stats = $("[data-card-stats]", card);
  const rows = new Map<number, Row>();
  const list = $("[data-transcript]", bout);
  if (list) for (const r of readRows(list)) rows.set(r.turn, r);

  const chart = $("[data-card-chart]", card),
    shape = $("[data-card-shape]", card),
    read = $("[data-card-read]", card);
  const turnEl = $("[data-card-turn]", card),
    shotEl = $("[data-card-shot]", card);
  let hot = 0; // the turn under the pointer, or none: the readout says the latest
  const draw = () => {
    const all = [...rows.values()].sort((a, b) => a.turn - b.turn);
    if (chart) chart.innerHTML = boutSvg(all, maxTokens, hot);
    if (shape) shape.innerHTML = shapeSvg(hot ? all.filter((r) => r.turn <= hot) : all); // under the pointer: the shapes as they stood then
    if (read) read.innerHTML = readout(hot ? rows.get(hot) : all[all.length - 1], maxTokens, tips);
    if (turnEl) turnEl.textContent = String(all.length ? all[all.length - 1].turn : 0);
    if (stats) fillStats(stats, all);
  };
  draw();

  // the shot clock: from the last turn to arrive (the stream's word, not the typewriter's), while the bout is live; nothing to time before the first turn (the opening ceremony)
  let lastIn = Date.now(),
    timing = rows.size > 0;
  const tick = () => {
    if (!shotEl) return;
    if (bout.dataset.verdict || sealed(bout)) {
      shotEl.textContent = "final";
      shotEl.classList.remove("late");
      return;
    }
    if (!live) {
      shotEl.textContent = "replay";
      return;
    }
    if (!timing) {
      shotEl.textContent = "—";
      return;
    }
    const s = (Date.now() - lastIn) / 1000;
    shotEl.textContent = `${s.toFixed(1)} s`;
    shotEl.classList.toggle("late", cap > 0 && s * 1000 > cap * LATE);
  };
  tick();
  const timer = setInterval(tick, TICK);

  // the pointer over the chart: its turn lit and read out; off it, the latest again
  if (chart) {
    chart.addEventListener(
      "pointermove",
      (e) => {
        const box = chart.getBoundingClientRect();
        const r = box.width ? turnAt([...rows.values()], (e.clientX - box.left) / box.width) : undefined;
        const t = r ? r.turn : 0;
        if (t !== hot) {
          hot = t;
          draw();
        }
      },
      { signal },
    );
    chart.addEventListener(
      "pointerleave",
      () => {
        if (hot) {
          hot = 0;
          draw();
        }
      },
      { signal },
    );
  }
  bout.addEventListener(
    "basho:turn",
    (e) => {
      const m = (e as CustomEvent<{ turn: TurnEvent }>).detail?.turn;
      if (m) {
        rows.set(m.turn, rowOf(m));
        draw();
      }
    },
    { signal },
  );
  bout.addEventListener(
    "basho:turn-in",
    () => {
      lastIn = Date.now();
      timing = true;
      tick();
    },
    { signal },
  );
  ring.onTeardown(() => {
    clearInterval(timer);
    ctrl.abort();
  });
}
